<template>
  <div class="payment-term-list-container payment-term-list-main payment-term-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Payment Term List']" />

    <div class="glass-tab-view glass-tab-view--flush payment-term-list-glass-shell">
      <div class="glass-tab-view__content payment-term-list-tab-content payment-term-list-page-stack">
        <div class="payment-term-list-header-block">
          <div class="payment-term-list-header-row">
            <div class="payment-term-list-header-row__text">
              <h2>Payment Terms</h2>
              <p class="payment-term-list-header-row__sub">Create and manage payment terms for customers and vendors</p>
            </div>
            <div class="payment-term-list-header-actions">
              <PrimaryButton label="Add New" @click="showModal = true" />
            </div>
          </div>
        </div>

        <div class="payment-term-list-glass-panel payment-term-list-main-card">
          <div class="consultant-glass-table-section payment-term-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table payment-term-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th class="payment-term-list-due-header">Due Days</th>
                      <th class="payment-term-list-delete-header">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="4" class="consultant-glass-table-no-records payment-term-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="4" class="consultant-glass-table-no-records payment-term-list-no-records payment-term-list-error">
                        {{ error }}
                      </td>
                    </tr>
                    <tr v-else-if="paymentTerms.length === 0">
                      <td colspan="4" class="consultant-glass-table-no-records payment-term-list-no-records">
                        No payment terms found.
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(term, idx) in paymentTerms"
                        :key="term.id"
                        class="consultant-glass-table-row payment-term-list-row"
                        :style="{ '--ptl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td>{{ term.name }}</td>
                        <td>{{ term.description }}</td>
                        <td>{{ term.dueDays || term.netDueIn || term.netDueBefore || 0 }}</td>
                        <td class="payment-term-list-delete-cell">
                          <SecondaryButton
                            label="Delete"
                            icon="bi bi-trash"
                            custom-class="consultant-secondary-btn--danger payment-term-list-delete-btn"
                            :disabled="isDeleting"
                            @click="openDeleteModal(term.id)"
                          />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddNewPaymentTerm
      :visible="showModal"
      :companyId="$route.params.id"
      :existingTerms="paymentTerms"
      @ok="handlePaymentTermCreated"
      @cancel="closeModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      buttonText="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      buttonText="OK"
      @close="closeErrorModal"
    />

    <WarningModal
      :show="showWarningModal"
      title="Delete Payment Term"
      :message="warningMessage"
      buttonText="Cancel"
      confirmButtonText="Delete"
      @close="closeWarningModal"
      @confirm="confirmDeletePaymentTerm"
    />
  </div>
</template>

<script>
import '../styles/PaymentTermList.css';
import AddNewPaymentTerm from './AddNewPaymentTerm.vue';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'PaymentTermList',
  components: {
    AddNewPaymentTerm,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      showModal: false,
      paymentTerms: [],
      loading: false,
      error: '',
      refreshInterval: null,
      termIdToDelete: null,
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      errorMessage: '',
      warningMessage: '',
      successMessage: '',
      isDeleting: false,
    };
  },
  computed: {
    hasPaymentTerms() {
      return this.paymentTerms && this.paymentTerms.length > 0;
    },
    canDelete() {
      return this.hasPaymentTerms && !this.isDeleting;
    },
  },
  methods: {
    async fetchPaymentTerms({ showLoading = true } = {}) {
      if (showLoading) this.loading = true;
      this.error = '';
      try {
        const companyId = this.$route.params.id;
        const res = await ApiService.get(`/company/payment-terms?companyId=${companyId}`);
        this.paymentTerms = Array.isArray(res.data.paymentTerms) ? res.data.paymentTerms : [];
        logger.log('Successfully fetched payment terms:', this.paymentTerms.length);
      } catch (err) {
        logger.error('Error fetching payment terms:', err);

        if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Payment terms service not found. Please contact support.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to view payment terms.';
        } else if (err.response?.status === 500) {
          this.errorMessage =
            'Internal server error occurred while fetching payment terms. The server encountered an unexpected condition. Please try again in a few minutes.';
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage = 'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while fetching payment terms. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = 'An unexpected error occurred while fetching payment terms. Please try again.';
        }
        this.showErrorModal = true;
      } finally {
        if (showLoading) this.loading = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.error = '';
    },
    async handlePaymentTermCreated(createdTerm) {
      this.closeModal();
      await this.fetchPaymentTerms();
      const termCount = this.paymentTerms.length;
      this.successMessage = `Payment term "${createdTerm?.name || 'New Payment Term'}" created successfully! You now have ${termCount} payment term${termCount !== 1 ? 's' : ''} in your system.`;
      this.showSuccessModal = true;
      logger.log('Payment term created successfully, total terms:', termCount);
    },
    openDeleteModal(termId) {
      const term = this.paymentTerms.find((t) => t.id === termId);
      if (!term || !term.id) {
        this.errorMessage = 'Invalid payment term selected. Please refresh the page and try again.';
        this.showErrorModal = true;
        return;
      }
      if (!term.name || term.name.trim() === '') {
        this.errorMessage = 'Cannot delete a payment term with an empty name.';
        this.showErrorModal = true;
        return;
      }
      this.termIdToDelete = termId;
      this.warningMessage = `Are you sure you want to delete the payment term "${term.name}"? This action cannot be undone and may affect vendors assigned to this payment term.`;
      this.showWarningModal = true;
      logger.log('Delete confirmation requested for payment term:', term.name);
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.termIdToDelete = null;
      this.warningMessage = '';
    },
    async confirmDeletePaymentTerm() {
      if (!this.termIdToDelete) return;

      this.isDeleting = true;
      this.showWarningModal = false;

      try {
        const term = this.paymentTerms.find((t) => t.id === this.termIdToDelete);
        logger.log('Attempting to delete payment term:', term?.name, 'ID:', this.termIdToDelete);

        await ApiService.delete(`/company/payment-terms/${this.termIdToDelete}`);
        await this.fetchPaymentTerms();

        const remainingCount = this.paymentTerms.length;
        this.successMessage = `Payment term "${term?.name}" has been deleted successfully. You now have ${remainingCount} payment term${remainingCount !== 1 ? 's' : ''} remaining.`;
        this.showSuccessModal = true;
        logger.log('Payment term deleted successfully, remaining terms:', remainingCount);
      } catch (err) {
        logger.error('Error deleting payment term:', err);

        const errorText =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          '';

        if (
          errorText.includes('foreign key constraint') ||
          errorText.includes('violates foreign key') ||
          errorText.includes('referenced by') ||
          errorText.includes('cannot delete') ||
          errorText.includes('constraint') ||
          errorText.includes('customers') ||
          errorText.includes('vendors') ||
          errorText.includes('paymentTermId') ||
          errorText.includes('PaymentTerms')
        ) {
          const term = this.paymentTerms.find((t) => t.id === this.termIdToDelete);
          if (errorText.includes('customers') || errorText.includes('customer')) {
            this.errorMessage = `Cannot delete "${term?.name}" because it is currently assigned to one or more customers. Please reassign those customers to another payment term first.`;
          } else if (errorText.includes('vendors') || errorText.includes('vendor')) {
            this.errorMessage = `Cannot delete "${term?.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another payment term first.`;
          } else {
            this.errorMessage = `Cannot delete "${term?.name}" because it is currently being used by other records. Please reassign those records to another payment term first.`;
          }
        } else if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 409) {
          const term = this.paymentTerms.find((t) => t.id === this.termIdToDelete);
          this.errorMessage = `Cannot delete "${term?.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another payment term first.`;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Payment term not found. It may have already been deleted.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to delete payment terms.';
        } else if (err.response?.status === 500) {
          const errText =
            err.response?.data?.message || err.response?.data?.error || err.response?.data?.detail || '';
          if (
            errText.includes('foreign key constraint') ||
            errText.includes('violates foreign key') ||
            errText.includes('referenced by') ||
            errText.includes('customers') ||
            errText.includes('vendors') ||
            errText.includes('paymentTermId') ||
            errText.includes('PaymentTerms')
          ) {
            const term = this.paymentTerms.find((t) => t.id === this.termIdToDelete);
            this.errorMessage = `Cannot delete "${term?.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another payment term first.`;
          } else {
            this.errorMessage =
              'Internal server error occurred while deleting the payment term. The server encountered an unexpected condition. Please try again in a few minutes.';
          }
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage = 'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while deleting the payment term. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          const term = this.paymentTerms.find((t) => t.id === this.termIdToDelete);
          this.errorMessage = `An unexpected error occurred while deleting "${term?.name}". Please try again.`;
        }
        this.showErrorModal = true;
      } finally {
        this.isDeleting = false;
        this.termIdToDelete = null;
        this.warningMessage = '';
      }
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
  },
  mounted() {
    this.fetchPaymentTerms();
    this.refreshInterval = setInterval(() => this.fetchPaymentTerms({ showLoading: false }), 10000);
  },
  beforeUnmount() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  },
};
</script>
